import { useState, useEffect } from 'react';
import { X, Users, ShieldCheck } from 'lucide-react';
import { createGroup } from '../../services/api';
import toast from 'react-hot-toast';

interface CreateGroupModalProps {
  clientId: string;
  onClose: () => void;
  onCreated: () => void;
}

export default function CreateGroupModal({ clientId, onClose, onCreated }: CreateGroupModalProps) {
  const [displayName, setDisplayName] = useState('');
  const [description, setDescription] = useState('');
  const [groupType, setGroupType] = useState<'security' | 'microsoft365'>('security');
  const [mailNickname, setMailNickname] = useState('');
  const [creating, setCreating] = useState(false);

  // Auto-generate mail nickname from display name for M365 groups
  useEffect(() => {
    if (groupType === 'microsoft365' && !mailNickname) {
      // Only auto-set if user hasn't manually edited
    }
  }, [groupType]);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    if (!displayName.trim()) {
      toast.error('Group name is required');
      return;
    }

    try {
      setCreating(true);
      const data: {
        displayName: string;
        description?: string;
        groupType: 'security' | 'microsoft365';
        mailNickname?: string;
      } = {
        displayName: displayName.trim(),
        description: description.trim() || undefined,
        groupType,
      };

      if (groupType === 'microsoft365') {
        data.mailNickname = mailNickname.trim() || displayName.trim().replace(/\s+/g, '').replace(/[^a-zA-Z0-9]/g, '');
      }

      const result = await createGroup(clientId, data);

      if (result.success) {
        toast.success(`Group "${displayName}" created successfully`);
        onCreated();
      } else {
        toast.error(result.error || 'Failed to create group');
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to create group';
      toast.error(msg);
    } finally {
      setCreating(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-card rounded-lg shadow-xl w-full max-w-md">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-border">
            <h2 className="text-lg font-semibold text-foreground">Create Group</h2>
            <button
              onClick={onClose}
              className="p-2 text-muted-foreground hover:text-foreground rounded-lg hover:bg-accent"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-4 space-y-4">
            {/* Group Name */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Group Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Enter group name"
                className="w-full px-3 py-2 border border-border rounded-lg bg-card text-foreground focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                disabled={creating}
                autoFocus
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Description
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Enter group description (optional)"
                rows={3}
                className="w-full px-3 py-2 border border-border rounded-lg bg-card text-foreground focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
                disabled={creating}
              />
            </div>

            {/* Group Type */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Group Type
              </label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setGroupType('security')}
                  disabled={creating}
                  className={`flex flex-col items-center gap-2 p-3 rounded-lg border-2 transition-colors ${
                    groupType === 'security'
                      ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
                      : 'border-border hover:border-muted-foreground'
                  }`}
                >
                  <ShieldCheck className={`h-6 w-6 ${groupType === 'security' ? 'text-green-600' : 'text-muted-foreground'}`} />
                  <span className={`text-sm font-medium ${groupType === 'security' ? 'text-green-700 dark:text-green-300' : 'text-muted-foreground'}`}>
                    Security
                  </span>
                  <span className="text-xs text-muted-foreground text-center">
                    Control access to resources
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => setGroupType('microsoft365')}
                  disabled={creating}
                  className={`flex flex-col items-center gap-2 p-3 rounded-lg border-2 transition-colors ${
                    groupType === 'microsoft365'
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                      : 'border-border hover:border-muted-foreground'
                  }`}
                >
                  <Users className={`h-6 w-6 ${groupType === 'microsoft365' ? 'text-blue-600' : 'text-muted-foreground'}`} />
                  <span className={`text-sm font-medium ${groupType === 'microsoft365' ? 'text-blue-700 dark:text-blue-300' : 'text-muted-foreground'}`}>
                    Microsoft 365
                  </span>
                  <span className="text-xs text-muted-foreground text-center">
                    Teams, shared mailbox, calendar
                  </span>
                </button>
              </div>
            </div>

            {/* Mail Nickname (M365 groups only) */}
            {groupType === 'microsoft365' && (
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Mail Nickname
                </label>
                <input
                  type="text"
                  value={mailNickname}
                  onChange={(e) => setMailNickname(e.target.value)}
                  placeholder={displayName.replace(/\s+/g, '').replace(/[^a-zA-Z0-9]/g, '') || 'autogenerated'}
                  className="w-full px-3 py-2 border border-border rounded-lg bg-card text-foreground focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  disabled={creating}
                />
                <p className="mt-1 text-xs text-muted-foreground">
                  Used for the group email address. Leave blank to auto-generate.
                </p>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                disabled={creating}
                className="flex-1 px-4 py-2 bg-muted text-muted-foreground rounded-lg hover:bg-accent transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={creating || !displayName.trim()}
                className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {creating ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Creating...
                  </>
                ) : (
                  'Create Group'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
